import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Label } from "../../../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../../../components/ui/radio-group";
import { Input } from "../../../../components/ui/input";
import { useNavigate, useLocation } from "react-router-dom";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { config } from "../../../../Environment";
import { Info, Bell, CheckCircle2, Calendar } from "lucide-react";
import { toast } from "sonner";
import { DateTime } from "luxon";
import {
  getActiveReminderForProcess,
  createCheckpointReminder,
  cancelReminder,
} from "../../../../api/checkpointReminders";
import { trackAppEvent } from "../../../../lib/appTracking";

const SaveAndContinueModal = ({
  isDialogOpen,
  setIsDialogOpen,
  processId,
  prescriptionId,
  isUserSignedIn,
  onSave,
  onDiscard,
  onAfterSaveOrDiscard, // Callback for navigation after save/discard
  activityTitle = "Activity", // Activity/prescription name for reminder
}) => {
  const { t } = useTranslation();
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Reminder state
  const [activeReminder, setActiveReminder] = useState(null);
  const [loadingReminder, setLoadingReminder] = useState(false);
  const [setReminderEnabled, setSetReminderEnabled] = useState(false);
  const [reminderAction, setReminderAction] = useState("resume_engagement");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("09:00");

  const navigate = useNavigate();
  const location = useLocation();
  const retryFetch = useRetryFetch(); // ✅ Hook called at the top level

  // Fetch active reminder when modal opens
  useEffect(() => {
    if (isDialogOpen && processId && isUserSignedIn) {
      fetchActiveReminder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogOpen, processId, isUserSignedIn]);

  // Initialize selectedDate to tomorrow by default
  useEffect(() => {
    if (isDialogOpen) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setSelectedDate(tomorrow);
      setSelectedTime("09:00");
    }
  }, [isDialogOpen]);

  const fetchActiveReminder = async () => {
    setLoadingReminder(true);
    try {
      const response = await getActiveReminderForProcess(retryFetch, processId);
      if (response.hasActiveReminder) {
        setActiveReminder(response.reminder);
      } else {
        setActiveReminder(null);
      }
    } catch (error) {
      console.error("Failed to fetch reminder:", error);
      // Don't show error toast, just fail silently
    } finally {
      setLoadingReminder(false);
    }
  };

  const handleCancelReminder = async () => {
    if (!activeReminder) return;

    try {
      await cancelReminder(retryFetch, activeReminder._id);
      toast.success(t("reminder_cancelled_success"));
      setActiveReminder(null);
    } catch (error) {
      console.error("Failed to cancel reminder:", error);
      toast.error(t("reminder_cancel_failed"));
    }
  };

  const setQuickDate = (daysFromNow) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    setSelectedDate(date);
  };

  async function onSaveEngagement() {
    setSaveLoading(true);
    try {
      // Call onSave (which now saves the current progress)
      await onSave();

      // If user enabled reminder and no reminder exists yet, create it
      if (setReminderEnabled && !activeReminder) {
        await createReminderForEngagement();
      }

      // Close the modal
      setIsDialogOpen(false);

      // Navigate after closing (if callback provided)
      if (onAfterSaveOrDiscard) {
        onAfterSaveOrDiscard();
      }
    } catch (err) {
      console.error("Unexpected error saving engagement:", err);
      alert("An unexpected error occurred. Please try again later.");
    } finally {
      setSaveLoading(false);
    }
  }

  async function createReminderForEngagement() {
    try {
      const [hours, minutes] = selectedTime.split(":").map(Number);
      const scheduledDateTime = new Date(selectedDate);
      scheduledDateTime.setHours(hours, minutes, 0, 0);

      // Validate that scheduled time is in the future
      if (scheduledDateTime <= new Date()) {
        toast.warning(t("reminder_future_time_warning"));
        throw new Error("Invalid reminder time");
      }

      await createCheckpointReminder(retryFetch, {
        processId,
        scheduledDateTime: scheduledDateTime.toISOString(),
        activityTitle: activityTitle,
        reminderAction: reminderAction,
        actionItems: [],
        insights: [],
      });

      const timeFromNow = DateTime.fromJSDate(scheduledDateTime).toRelative();
      toast.success(t("reminder_set_success", { timeFromNow }));
    } catch (error) {
      console.error("Failed to create reminder:", error);
      if (!error.message.includes("Invalid reminder time")) {
        toast.error(t("reminder_set_failed"));
      }
      throw error; // Re-throw to prevent modal from closing
    }
  }

  async function onDiscardEngagement() {
    setDeleteLoading(true);
    try {
      onDiscard();
      const response = await retryFetch(
        `${config.API_BASE}/api/processes/remove/${processId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Failed to delete press:", errorData);
        return;
      }

      const data = await response.json();
      console.log("Successfully deleted:", data);

      trackAppEvent("engagement_terminated", {
        processId,
        prescriptionId,
        activityName: activityTitle,
      });

      // Close the modal
      setIsDialogOpen(false);

      // Navigate after closing (if callback provided)
      if (onAfterSaveOrDiscard) {
        onAfterSaveOrDiscard();
      }
    } catch (err) {
      console.error("Unexpected error deleting process:", err);
    } finally {
      setDeleteLoading(false);
    }
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("engagement_exit_without_completion_dialog_title")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {isUserSignedIn ? (
            <div className="space-y-4">
              {/* Reminder Section */}
              <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-primary" />
                  <h4 className="font-medium text-sm">{t("reminder_section_title")}</h4>
                </div>

                {loadingReminder ? (
                  <p className="text-sm text-muted-foreground">{t("reminder_loading")}</p>
                ) : activeReminder ? (
                  // Show existing reminder
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="font-medium">{t("reminder_already_set")}</span>
                    </div>
                    <div className="pl-6 space-y-1">
                      <p className="text-sm">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {DateTime.fromISO(activeReminder.scheduledDateTime).toLocaleString(
                          DateTime.DATETIME_MED
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Type:{" "}
                        {activeReminder.reminderAction === "resume_engagement"
                          ? t("reminder_type_resume")
                          : t("reminder_type_start_new")}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleCancelReminder}
                      className="h-8"
                    >
                      {t("reminder_cancel_button")}
                    </Button>
                  </div>
                ) : (
                  // No reminder - show option to set one
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="set-reminder"
                        checked={setReminderEnabled}
                        onCheckedChange={setSetReminderEnabled}
                      />
                      <Label htmlFor="set-reminder" className="cursor-pointer text-sm">
                        {t("reminder_set_checkbox_label")}
                      </Label>
                    </div>

                    {setReminderEnabled && (
                      <div className="space-y-3 pl-6 animate-in slide-in-from-top-2">
                        {/* Reminder Type Selection */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            {t("reminder_action_prompt")}
                          </Label>
                          <RadioGroup
                            value={reminderAction}
                            onValueChange={setReminderAction}
                            className="space-y-2"
                          >
                            <div className="flex items-start space-x-2">
                              <RadioGroupItem value="resume_engagement" id="resume" />
                              <Label htmlFor="resume" className="cursor-pointer leading-normal">
                                <div className="font-medium text-sm">
                                  {t("reminder_type_resume")}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {t("reminder_type_resume_desc")}
                                </div>
                              </Label>
                            </div>
                            <div className="flex items-start space-x-2">
                              <RadioGroupItem
                                value="start_new_engagement"
                                id="start-new"
                              />
                              <Label htmlFor="start-new" className="cursor-pointer leading-normal">
                                <div className="font-medium text-sm">
                                  {t("reminder_type_start_new")}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {t("reminder_type_start_new_desc")}
                                </div>
                              </Label>
                            </div>
                          </RadioGroup>
                        </div>

                        {/* Quick Date Selection */}
                        <div className="space-y-2">
                          <Label className="text-sm">{t("reminder_when_prompt")}</Label>
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => setQuickDate(1)}
                              className="h-8 text-xs"
                            >
                              {t("reminder_quick_tomorrow")}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => setQuickDate(2)}
                              className="h-8 text-xs"
                            >
                              {t("reminder_quick_2days")}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => setQuickDate(7)}
                              className="h-8 text-xs"
                            >
                              {t("reminder_quick_1week")}
                            </Button>
                          </div>
                        </div>

                        {/* Time Selection */}
                        <div className="space-y-2">
                          <Label htmlFor="reminder-time" className="text-sm">
                            {t("reminder_time_label")}
                          </Label>
                          <Input
                            id="reminder-time"
                            type="time"
                            value={selectedTime}
                            onChange={(e) => setSelectedTime(e.target.value)}
                            className="w-full"
                          />
                        </div>

                        {/* Preview */}
                        <div className="text-xs text-muted-foreground">
                          {t("reminder_preview_label")}{" "}
                          {DateTime.fromJSDate(selectedDate)
                            .set({
                              hour: parseInt(selectedTime.split(":")[0]),
                              minute: parseInt(selectedTime.split(":")[1]),
                            })
                            .toRelative()}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Warning Message */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-900">
                  {t("save_engagement_exit_heading")}
                </p>
              </div>

              {/* Save Button - Primary action */}
              <Button
                className="w-full"
                onClick={onSaveEngagement}
                disabled={saveLoading || deleteLoading}
              >
                {saveLoading ? "Saving..." : t("save_engagement_exit_btn_text")}
              </Button>

              {/* Discard Option - Bottom, less prominent */}
              <div className="text-center pt-4 border-t">
                <p className="text-xs text-muted-foreground mb-2">
                  {t("discard_engagement_exit_heading")}
                </p>
                <Button
                  size="xs"
                  variant="ghost"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 text-xs"
                  onClick={onDiscardEngagement}
                  disabled={deleteLoading || saveLoading}
                >
                  {t("discard_engagement_exit_btn_text")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
                <Info className="h-8 w-8 text-yellow-600" />
              </div>
              <div>
                <p className="font-medium mb-1 text-yellow-800">
                  {t("engagement_completed_heading_for_non_logged_in_user")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("engagement_completed_description_for_non_logged_in_user")}
                </p>
              </div>
              <div className="space-y-2">
                <Button
                  className="w-full"
                  onClick={async () => {
                    // Save engagement before navigating to login
                    if (onSave) {
                      await onSave();
                    }

                    // Navigate to login with engagement transfer context
                    navigate("/login", {
                      state: {
                        from: location,
                        engagementTransfer: {
                          processId,
                          prescriptionId,
                        },
                      },
                    });
                  }}
                >
                  {t("login")}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={async () => {
                    // Save engagement before navigating to register
                    if (onSave) {
                      await onSave();
                    }

                    // Navigate to register with engagement transfer context
                    navigate("/register", {
                      state: {
                        from: location,
                        engagementTransfer: {
                          processId,
                          prescriptionId,
                        },
                      },
                    });
                  }}
                >
                  {t("register_new_user")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SaveAndContinueModal;
